import React, { useState, useEffect } from 'react';
import { X, Check, Trash2, AlertCircle, Loader2, Wifi, WifiOff, Info, ChevronDown } from 'lucide-react';
import { useSkipCategories } from '../../context/SkipCategoriesContext';
import { useApiSync } from '../../hooks/useApiSync';

/**
 * Modal for managing skip categories
 * Shows checkboxes for each category with descriptions
 * Includes quick actions and sync status
 */
export default function SkipCategoriesModal({ tweetId, tweetSource, isOpen, onClose }) {
  const {
    getSkipCategories,
    updateSkipCategories,
    clearSkipCategories,
    markAsManualSkip,
    SKIP_CATEGORIES,
    SKIP_CATEGORY_LABELS,
    SKIP_CATEGORY_DESCRIPTIONS
  } = useSkipCategories();

  const { isConnected, queuedCount } = useApiSync();

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showPlatformInfo, setShowPlatformInfo] = useState(false);

  // Initialize with current categories
  useEffect(() => {
    if (isOpen) {
      const current = getSkipCategories(tweetId);
      setSelectedCategories(current);
      setError(null);
      setShowSuccess(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tweetId]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleToggleCategory = (category) => {
    setSelectedCategories(prev =>
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setError(null);

    const result = await updateSkipCategories(tweetId, selectedCategories, tweetSource, true);

    if (result.success || result.queued) {
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 500);
    } else {
      setError(result.error);
    }

    setIsSaving(false);
  };

  const handleClearAll = async () => {
    setSelectedCategories([]);
  };

  const handleQuickManualSkip = async () => {
    setSelectedCategories([SKIP_CATEGORIES.MANUAL]);
  };

  const hasChanges = () => {
    const current = getSkipCategories(tweetId);
    if (current.length !== selectedCategories.length) return true;
    return !current.every(cat => selectedCategories.includes(cat));
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onPointerMove={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div
        className="w-full max-w-md bg-white dark:bg-gray-900 rounded-xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Skip Categories
          </h2>

          <div className="flex items-center gap-3">
            {/* Connection status */}
            <div className="flex items-center gap-1.5 text-sm">
              {isConnected ? (
                <>
                  <Wifi className="w-4 h-4 text-green-600 dark:text-green-400" />
                  <span className="text-gray-600 dark:text-gray-400">Online</span>
                </>
              ) : (
                <>
                  <WifiOff className="w-4 h-4 text-orange-600 dark:text-orange-400" />
                  <span className="text-gray-600 dark:text-gray-400">Offline</span>
                </>
              )}
              {queuedCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs font-medium">
                  {queuedCount} queued
                </span>
              )}
            </div>

            <button
              onClick={onClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* Quick Actions */}
          <div className="flex gap-2">
            <button
              onClick={handleClearAll}
              disabled={selectedCategories.length === 0}
              className="
                flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg
                bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300
                hover:bg-gray-200 dark:hover:bg-gray-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors text-sm font-medium
              "
            >
              <Trash2 className="w-4 h-4" />
              Clear All
            </button>

            <button
              onClick={handleQuickManualSkip}
              className="
                flex-1 flex items-center justify-center gap-2 px-3 py-2 rounded-lg
                bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400
                hover:bg-red-200 dark:hover:bg-red-900/50
                transition-colors text-sm font-medium
              "
            >
              <X className="w-4 h-4" />
              Manual Skip
            </button>
          </div>

          {/* Platform Filtering Info - Collapsible */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowPlatformInfo(!showPlatformInfo)}
              className="w-full flex items-center justify-between p-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                  Platform Filtering
                </span>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-blue-600 dark:text-blue-400 transition-transform ${showPlatformInfo ? 'rotate-180' : ''}`}
              />
            </button>

            {showPlatformInfo && (
              <div className="p-3 bg-blue-50/50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800 text-sm animate-fadeIn">
                <div className="text-blue-700 dark:text-blue-300">
                  <span className="font-medium">ESP32</span> and <span className="font-medium">TRMNL</span> formats automatically apply e-ink profile filtering, which excludes:
                </div>
                <ul className="mt-1.5 space-y-0.5 text-blue-700 dark:text-blue-300">
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500 dark:bg-blue-400"></span>
                    <span><span className="font-medium">Image Only:</span> Content that's primarily images/media</span>
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500 dark:bg-blue-400"></span>
                    <span><span className="font-medium">Low Quality:</span> Low-engagement or promotional content</span>
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500 dark:bg-blue-400"></span>
                    <span><span className="font-medium">Long Text:</span> Very long content (&gt;500 characters)</span>
                  </li>
                  <li className="flex items-center gap-1.5">
                    <span className="w-1 h-1 rounded-full bg-blue-500 dark:bg-blue-400"></span>
                    <span><span className="font-medium">Manual:</span> Manually marked for skipping</span>
                  </li>
                </ul>
              </div>
            )}
          </div>

          {/* Category Checkboxes */}
          <div className="space-y-2">
            {Object.values(SKIP_CATEGORIES).map(category => {
              const isSelected = selectedCategories.includes(category);

              return (
                <label
                  key={category}
                  className={`
                    flex items-start gap-3 p-3 rounded-lg cursor-pointer
                    transition-all duration-200
                    ${isSelected
                      ? 'bg-red-50 dark:bg-red-900/20 border-2 border-red-300 dark:border-red-700'
                      : 'bg-gray-50 dark:bg-gray-800 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-700'
                    }
                  `}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleToggleCategory(category)}
                    className="
                      mt-0.5 w-5 h-5 rounded border-gray-300 dark:border-gray-600
                      text-red-600 focus:ring-red-500 focus:ring-offset-0
                      cursor-pointer
                    "
                  />

                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {SKIP_CATEGORY_LABELS[category]}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                      {SKIP_CATEGORY_DESCRIPTIONS[category]}
                    </div>
                  </div>

                  {isSelected && (
                    <Check className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  )}
                </label>
              );
            })}
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm text-red-700 dark:text-red-300">
                {error}
              </div>
            </div>
          )}

          {/* Success Message */}
          {showSuccess && (
            <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
              <div className="text-sm text-green-700 dark:text-green-300 font-medium">
                Skip categories updated successfully
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-4 border-t border-gray-200 dark:border-gray-800">
          <button
            onClick={onClose}
            className="
              flex-1 px-4 py-2 rounded-lg
              bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300
              hover:bg-gray-200 dark:hover:bg-gray-700
              transition-colors font-medium
            "
          >
            Cancel
          </button>

          <button
            onClick={handleSave}
            disabled={isSaving || !hasChanges()}
            className="
              flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg
              bg-blue-600 text-white
              hover:bg-blue-700
              disabled:opacity-50 disabled:cursor-not-allowed
              transition-colors font-medium
            "
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
